import type { AiCostDimension } from '@Application/AiCosts/AiCostDimension';
import PlainDate from '@Domain/PlainDate';
import AbstractFilterView, { type AbstractFilterViewArgs } from '@UI/Helpers/Filters/AbstractFilterView';

export type AiCostsTab = AiCostDimension | 'calls';
export type AiCostsSort = 'recent' | 'cost';

export type AiCostsFilterViewArgs = AbstractFilterViewArgs & {
	readonly from: PlainDate;
	readonly to: PlainDate;
	readonly tab: AiCostsTab;
	readonly organizationId: number | null;
	readonly facilityId: number | null;
	readonly userId: number | null;
	readonly sort: AiCostsSort;
};

export type AiCostsDrillDownScope = { organizationId: number } | { facilityId: number };

const SUBJECT_TABS: readonly AiCostsTab[] = ['facilities', 'organizations', 'users'];
const SORTED_TABS: readonly AiCostsTab[] = ['documents', 'interviews'];

/**
 * The period, the active tab and the scope (structure, facility, user) of the costs screen. Both
 * period bounds are always set. The scope only applies to the subject tabs; paging is owned by the
 * Queries, so `queryParams` of the base class is not used.
 */
class AiCostsFilterView extends AbstractFilterView<AiCostsFilterViewArgs> {
	readonly from: PlainDate;
	readonly to: PlainDate;
	readonly tab: AiCostsTab;
	readonly organizationId: number | null;
	readonly facilityId: number | null;
	readonly userId: number | null;
	readonly sort: AiCostsSort;

	constructor(args: AiCostsFilterViewArgs) {
		super(args);

		this.from = args.from;
		this.to = args.to;
		this.tab = args.tab;
		this.organizationId = args.organizationId;
		this.facilityId = args.facilityId;
		this.userId = args.userId;
		this.sort = args.sort;

		Object.freeze(this);
	}

	/** From the first day of the current month to today, on the facilities tab, without scope. */
	static createDefault(): AiCostsFilterView {
		const today = PlainDate.today();

		return new AiCostsFilterView({
			from: new PlainDate(today.year, today.month, 1),
			to: today,
			tab: 'facilities',
			organizationId: null,
			facilityId: null,
			userId: null,
			sort: 'recent',
		});
	}

	get isSubjectTab(): boolean {
		return SUBJECT_TABS.includes(this.tab);
	}

	get hasScope(): boolean {
		return (
			this.isSubjectTab &&
			(this.organizationId !== null || this.facilityId !== null || this.userId !== null)
		);
	}

	/** One per criterion that differs from the default; the sort is not a filter. */
	get activeFilterCount(): number {
		const defaults = AiCostsFilterView.createDefault();
		const isDefaultPeriod =
			this.from.toApi() === defaults.from.toApi() && this.to.toApi() === defaults.to.toApi();

		return (
			(isDefaultPeriod ? 0 : 1) +
			(this.organizationId === null ? 0 : 1) +
			(this.facilityId === null ? 0 : 1) +
			(this.userId === null ? 0 : 1)
		);
	}

	/** Period only: the key of the reference summary that ignores the scope. */
	get periodQueryParams(): URLSearchParams {
		const params = new URLSearchParams();
		params.set('from', this.from.toApi());
		params.set('to', this.to.toApi());

		return params;
	}

	get summaryQueryParams(): URLSearchParams {
		return this.isSubjectTab ? this.withScope(this.periodQueryParams) : this.periodQueryParams;
	}

	/** Same period and scope as the summary, plus the sort the item tabs of the workbook follow. */
	get exportQueryParams(): URLSearchParams {
		const params = this.summaryQueryParams;
		params.set('sort', this.sort);

		return params;
	}

	/** Without `page` and `rows`, which the Queries add. */
	get tabQueryParams(): URLSearchParams {
		if (this.isSubjectTab) {
			return this.withScope(this.periodQueryParams);
		}

		const params = this.periodQueryParams;

		if (SORTED_TABS.includes(this.tab)) {
			params.set('sort', this.sort);
		}

		return params;
	}

	setPeriod(from: PlainDate, to: PlainDate): this {
		return this.cloneWith({ from, to });
	}

	setTab(tab: AiCostsTab): this {
		return this.cloneWith({ tab, organizationId: null, facilityId: null, userId: null });
	}

	setOrganization(organizationId: number | null): this {
		return this.cloneWith({ organizationId, facilityId: null, userId: null });
	}

	setFacility(facilityId: number | null): this {
		return this.cloneWith({ facilityId, userId: null });
	}

	setUser(userId: number | null): this {
		return this.cloneWith({ userId });
	}

	clearScope(): this {
		return this.cloneWith({ organizationId: null, facilityId: null, userId: null });
	}

	setSort(sort: AiCostsSort): this {
		return this.cloneWith({ sort });
	}

	drillDown(tab: AiCostsTab, scope: AiCostsDrillDownScope): this {
		return this.cloneWith({
			tab,
			organizationId: 'organizationId' in scope ? scope.organizationId : null,
			facilityId: 'facilityId' in scope ? scope.facilityId : null,
			userId: null,
		});
	}

	resetToCurrentMonth(): this {
		const { from, to } = AiCostsFilterView.createDefault();

		return this.cloneWith({ from, to });
	}

	private withScope(params: URLSearchParams): URLSearchParams {
		if (this.organizationId !== null) {
			params.set('organizationId', String(this.organizationId));
		}

		if (this.facilityId !== null) {
			params.set('facilityId', String(this.facilityId));
		}

		if (this.userId !== null) {
			params.set('userId', String(this.userId));
		}

		return params;
	}
}

export default AiCostsFilterView;
